// Renders an assistant answer: paragraphs, lists, **bold**, `code`, and [[type:id]] citations as
// links. Built as React elements (never innerHTML), so an answer cannot inject markup.
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { ResolvedRef } from '../lib/types';

const INLINE = /(\[\[(?:project|knowledge|decision|task|repo):[0-9a-fA-F-]{36}\]\]|\*\*[^*]+\*\*|`[^`]+`)/g;

export function refHref(r: ResolvedRef): string | null {
  // An idea without a workspace lives on the personal Ideas page.
  if (r.available && r.id && r.type === 'idea') return r.workspace ? `/w/${r.workspace}/ideas?open=${r.id}` : `/ideas?open=${r.id}`;
  if (!r.available || !r.workspace || !r.id) return null;
  const base = `/w/${r.workspace}`;
  if (r.type === 'meeting') return `${base}/meetings/${r.id}`;
  if (r.type === 'project') return `${base}/projects/${r.id}`;
  if (r.type === 'decision') return `${base}/decisions?open=${r.id}`;
  if (r.type === 'task') return r.project_id ? `${base}/projects/${r.project_id}?task=${r.id}` : `${base}/projects?tab=tasks&open=${r.id}`;
  return r.project_id ? `${base}/projects/${r.project_id}?knowledge=${r.id}` : `${base}/projects?tab=knowledge&open=${r.id}`;
}

function CitationChip({ refId, resolved }: { refId: string; resolved: Map<string, ResolvedRef> }) {
  const r = resolved.get(refId);
  const kind = refId.split(':')[0];
  if (!r) return <span className="cite pending">{kind}</span>;
  if (r.available && r.url) return <RepoLink r={r} />;
  const href = refHref(r);
  if (!href) {
    return (
      <span className="cite gone" title="This record is no longer available to you">
        {kind} · no longer available
      </span>
    );
  }
  return (
    <Link className="cite" to={href} title={`${kind}: ${r.title}`}>
      {kind} · {r.title}
    </Link>
  );
}

/** A repository cited in an answer: its GitHub page, in a new tab. */
export function RepoLink({ r }: { r: ResolvedRef }) {
  return (
    <a className="cite" href={r.url} target="_blank" rel="noopener noreferrer" title={`repository: ${r.title}`}>
      repo · {r.title}
    </a>
  );
}

function inline(text: string, resolved: Map<string, ResolvedRef>, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  let i = 0;
  for (const part of text.split(INLINE)) {
    if (!part) continue;
    const k = `${key}-${i++}`;
    if (part.startsWith('[[')) out.push(<CitationChip key={k} refId={part.slice(2, -2).toLowerCase()} resolved={resolved} />);
    else if (part.startsWith('**')) out.push(<strong key={k}>{part.slice(2, -2)}</strong>);
    else if (part.startsWith('`')) out.push(<code key={k}>{part.slice(1, -1)}</code>);
    else out.push(part);
  }
  return out;
}

export function Answer({ text, resolved }: { text: string; resolved: Map<string, ResolvedRef> }) {
  const blocks: ReactNode[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];
  const flushPara = () => {
    if (para.length) blocks.push(<p key={`p${blocks.length}`}>{inline(para.join(' '), resolved, `p${blocks.length}`)}</p>);
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const items = list.items.map((it, j) => <li key={j}>{inline(it, resolved, `l${blocks.length}-${j}`)}</li>);
    blocks.push(list.ordered ? <ol key={`l${blocks.length}`}>{items}</ol> : <ul key={`l${blocks.length}`}>{items}</ul>);
    list = null;
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    const heading = /^#{1,4}\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      flushPara();
      const ordered = Boolean(numbered);
      if (list && list.ordered !== ordered) flushList();
      if (!list) list = { ordered, items: [] };
      list.items.push((bullet ?? numbered)![1]);
    } else if (heading) {
      flushPara();
      flushList();
      blocks.push(<h3 key={`h${blocks.length}`}>{inline(heading[1], resolved, `h${blocks.length}`)}</h3>);
    } else if (!line.trim()) {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  return <div className="answer">{blocks}</div>;
}
